import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  MenuItem,
  Switch,
  FormControlLabel,
  FormControl,
  Divider,
  TextFieldProps,
  InputAdornment,
  SelectChangeEvent,
} from "@mui/material";
import {
  X as XIcon,
  Plus,
  GripVertical,
  ChevronDown,
  Circle,
  CircleCheck,
  Trash2,
} from "lucide-react";
import { FormField, FormStructure } from "./FormRenderer";
import { SingleSortableList } from "../common/dnd/SingleSortableList";
import { DragHandle } from "../common/dnd/components/Item";
import TextField from "../common/TextField";
import Select from "../common/Select";
import { useSnackpack } from "../context/SnackpackContextProvider";
import FlexBox from "components/common/FlexBox";
import useIsMobile from "components/hooks/useIsMobile";
import useIsDesktop from "components/hooks/useIsDesktop";
import FormHeader from "./FormHeader";
import { DocumentScope } from "protogen/documents_pb";

// Reusable borderless TextField component
interface BorderlessTextFieldProps extends Omit<TextFieldProps, "variant"> {
  variant?: "filled" | "outlined" | "standard" | "detachedOutlined";
}

const BorderlessTextField = (props: BorderlessTextFieldProps) => {
  return (
    <TextField
      {...props}
      sx={{
        "& .MuiInputBase-root": {
          border: "none !important",
        },
        "& .MuiInputBase-input": { paddingLeft: "0px" },
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none !important",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          border: "none !important",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          border: "none !important",
        },
        "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
          border: "none !important",
        },
        "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          border: "none !important",
        },
        ...props.sx,
      }}
    />
  );
};

const FIELD_TYPES = [
  { value: "text", label: "Short answer" },
  { value: "paragraph", label: "Paragraph" },
  { value: "radio", label: "Single choice" },
  { value: "checkbox", label: "Multiple choice" },
  { value: "yesno", label: "Yes/No" },
  { value: "location", label: "Location" },
  { value: "attachment", label: "File upload" },
];

const defaultField = (): FormField => {
  return {
    id: Math.random().toString(36).slice(2),
    type: "text",
    text: "",
    multiline: false,
    value: "",
  };
};

interface FormCreatorProps {
  form: FormStructure;
  onChange: (form: FormStructure) => void;
  uuid: string;
  onSubmit: () => void;
  isSubmitting: boolean;
  viewMode: "edit" | "view" | "results";
  setViewMode: (mode: "edit" | "view" | "results") => void;
  additionalOptions?: React.ReactNode;
  scope: DocumentScope;
}

const FieldCard = ({
  field,
  onChange,
  onRemove,
}: {
  field: FormField;
  onChange: (field: FormField) => void;
  onRemove: () => void;
}) => {
  const isDesktop = useIsDesktop();
  const handleFieldChange = (key: string, value: any) => {
    onChange({ ...field, [key]: value });
  };

  const handleOptionChange = (i: number, key: string, value: string) => {
    if (field.type === "radio" || field.type === "checkbox") {
      const newOptions = field.options.map((opt, idx) => {
        if (idx === i) {
          const updatedOpt = { ...opt, [key]: value };
          // Auto-generate value when label changes
          if (key === "label") {
            updatedOpt.value = `${value.toLowerCase().replace(/\s+/g, "_")}_${
              idx + 1
            }`;
          }
          return updatedOpt;
        }
        return opt;
      });

      // If this is the last option and it now has a value, add a new empty option
      const isLastOption = i === field.options.length - 1;
      const isAddingValue =
        key === "label" &&
        value.trim() !== "" &&
        field.options[i].label.trim() === "";

      if (isLastOption && isAddingValue) {
        newOptions.push({
          value: "",
          label: "",
        });
      }

      onChange({ ...field, options: newOptions } as FormField);
    }
  };
  const handleRemoveOption = (i: number) => {
    if (field.type === "radio" || field.type === "checkbox") {
      const newOptions = field.options.filter((_, idx) => idx !== i);

      // Ensure we always have at least one empty option at the end
      const hasEmptyOption = newOptions.some((opt) => opt.label.trim() === "");
      if (!hasEmptyOption) {
        newOptions.push({ value: "", label: "" });
      }

      onChange({ ...field, options: newOptions } as FormField);
    }
  };

  const handleFieldTypeChange = (newType: string) => {
    const currentDisplayType =
      field.type === "text"
        ? field.multiline
          ? "paragraph"
          : "text"
        : field.type === "radio" &&
            field.options?.length === 2 &&
            field.options[0]?.label === "Yes" &&
            field.options[1]?.label === "No"
          ? "yesno"
          : field.type;

    if (newType === currentDisplayType) return;

    // Convert field to new type while preserving common properties
    const baseField = {
      id: field.id,
      text: field.text,
      subtext: field.subtext,
      required: field.required,
    };

    let convertedField: FormField;

    switch (newType) {
      case "text":
        convertedField = {
          ...baseField,
          type: "text",
          multiline: false,
          value: "",
        };
        break;
      case "paragraph":
        convertedField = {
          ...baseField,
          type: "text",
          multiline: true,
          value: "",
        };
        break;
      case "radio":
        convertedField = {
          ...baseField,
          type: "radio",
          options: [
            { value: "", label: "" },
            { value: "", label: "" },
          ],
          value: "",
        };
        break;
      case "yesno":
        convertedField = {
          ...baseField,
          type: "radio",
          options: [
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" },
          ],
          value: "",
        };
        break;
      case "checkbox":
        convertedField = {
          ...baseField,
          type: "checkbox",
          options: [
            { value: "", label: "" },
            { value: "", label: "" },
            { value: "", label: "" },
          ],
          value: [],
          maxSelect: undefined,
        };
        break;
      case "location":
        convertedField = {
          ...baseField,
          type: "location",
          value: undefined,
        };
        break;
      case "attachment":
        convertedField = {
          ...baseField,
          type: "attachment",
          value: [],
          maxFiles: 10,
        };
        break;
      default:
        return;
    }

    onChange(convertedField);
  };

  const isMobile = useIsMobile();

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "12px",
        backgroundColor: "#FAF9FA",
        padding: "16px 16px 16px 8px",
        display: "flex",
        gap: "8px",
      }}
    >
      <DragHandle>
        <GripVertical size={20} color="#8E9598" />
      </DragHandle>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        flexGrow={1}
        gap={"8px"}
      >
        <Box
          sx={(theme) => ({
            padding: "0px 16px",
            borderRadius: "8px",
            border: `1px solid ${theme.palette.border}`,
            backgroundColor: "#FFF",
          })}
        >
          <BorderlessTextField
            variant="detachedOutlined"
            placeholder="Question"
            size="small"
            value={field.text}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange("text", e.target.value)
            }
            fullWidth
            multiline
            sx={{
              "& .MuiInputBase-input": {
                fontSize: "16px",
                fontWeight: "600",
                paddingLeft: "0px",
                margin: "16px 0",
                whiteSpace: "pre-line",
                wordBreak: "break-word",
              },
            }}
          />
          <Divider />
          <BorderlessTextField
            size="small"
            variant="detachedOutlined"
            placeholder="Description (optional)"
            value={field.subtext || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange("subtext", e.target.value)
            }
            fullWidth
            multiline
            sx={{
              "& .MuiInputBase-input": {
                paddingLeft: "0px",
                margin: "12px 0",
                whiteSpace: "pre-line",
                wordBreak: "break-word",
              },
            }}
          />
        </Box>
        {(field.type === "radio" || field.type === "checkbox") && (
          <Box
            sx={(theme) => ({
              border: `1px solid ${theme.palette.border}`,
              borderRadius: "8px",
              overflow: "hidden",
            })}
          >
            {field.options.map((opt, i) => (
              <Box key={i}>
                <TextField
                  variant="detachedOutlined"
                  placeholder="Label"
                  value={opt.label}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleOptionChange(i, "label", e.target.value)
                  }
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      border: "none !important",
                      borderRadius: "0px !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none !important",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{
                          paddingLeft: "16px",
                          marginRight: "-8px",
                        }}
                      >
                        {field.type === "checkbox" ? (
                          <CircleCheck size={18} color="#D4D4D4" />
                        ) : (
                          <Circle size={18} color="#D4D4D4" />
                        )}
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {/* Don't show X button for last empty option */}
                        {!(
                          i === field.options.length - 1 &&
                          opt.label.trim() === ""
                        ) && (
                          <IconButton
                            onClick={() => handleRemoveOption(i)}
                            size="small"
                            disabled={
                              field.options.filter(
                                (opt) => opt.label.trim() !== "",
                              ).length <= 1
                            }
                            edge="end"
                            sx={{
                              // For desktop, only show on hover
                              ...(isDesktop && {
                                opacity: 0,
                                transition: "opacity 0.2s ease-in-out",
                                ".MuiTextField-root:hover &": {
                                  opacity: 1,
                                },
                              }),
                            }}
                          >
                            <XIcon size={18} stroke="#8E9598" />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                {i < field.options.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        )}
        <FlexBox stackedMobile gap="8px" justifyContent="space-between">
          <FlexBox gap="8px">
            <FormControl>
              <Select
                value={
                  field.type === "text"
                    ? field.multiline
                      ? "paragraph"
                      : "text"
                    : field.type === "radio" &&
                        field.options?.length === 2 &&
                        field.options[0]?.label === "Yes" &&
                        field.options[1]?.label === "No"
                      ? "yesno"
                      : field.type
                }
                onChange={(e: SelectChangeEvent<unknown>) =>
                  handleFieldTypeChange(e.target.value as string)
                }
                displayEmpty
                variant="outlined"
                IconComponent={ChevronDown}
                sx={{
                  width: "155px",
                  fontSize: "14px",
                  "& .MuiSelect-select.MuiInputBase-input": {
                    paddingRight: "4px",
                  },
                  "& .MuiOutlinedInput-input": {
                    color: "text.primary",
                  },
                }}
              >
                {FIELD_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {field.type === "checkbox" && (
              <TextField
                variant="detachedOutlined"
                size="small"
                label="Max #"
                type="number"
                value={field.maxSelect ?? ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFieldChange(
                    "maxSelect",
                    e.target.value === "" ? undefined : Number(e.target.value),
                  )
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: "40px",
                  },
                  width: "80px",
                  "&.MuiTextField-root": {
                    marginTop: 0,
                  },
                  label: {
                    marginTop: "-9px",
                  },
                }}
                inputProps={{ min: 1 }}
              />
            )}
          </FlexBox>
          <FlexBox
            sx={{
              justifyContent: isMobile ? "space-between" : "end",
              width: "100%",
              alignItems: "center",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={field.required || false}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange("required", e.target.checked)
                  }
                  sx={{ marginLeft: "12px" }}
                />
              }
              label="Required"
              labelPlacement="start"
            />
            {isMobile && (
              <IconButton onClick={onRemove}>
                <Trash2 size={20} stroke="#8E9598" />
              </IconButton>
            )}
          </FlexBox>
        </FlexBox>
      </Box>
      {!isMobile && (
        <Box>
          <IconButton onClick={onRemove}>
            <XIcon color="#8E9598" />
          </IconButton>
        </Box>
      )}
    </Paper>
  );
};

const FormCreator = ({
  form,
  onChange,
  uuid,
  onSubmit,
  isSubmitting,
  viewMode,
  setViewMode,
  additionalOptions,
  scope,
}: FormCreatorProps) => {
  const { pushSnack } = useSnackpack();

  // All handlers update via onChange
  const handleMetaChange = (
    key: keyof Omit<FormStructure, "fields">,
    value: string,
  ) => {
    onChange({ ...form, [key]: value });
  };

  const handleFieldChange = (fieldId: string, newField: FormField) => {
    const newFields = form.fields.map((field) =>
      field.id === fieldId ? newField : field,
    );
    onChange({ ...form, fields: newFields });
  };

  const handleRemoveField = (fieldId: string) => {
    const newFields = form.fields.filter((field) => field.id !== fieldId);
    onChange({ ...form, fields: newFields });
  };

  const handleDragReorder = (
    newItems: { id: string | number; content: React.ReactNode }[],
  ) => {
    const reorderedFields = newItems
      .map((item) => {
        return form.fields.find((field) => field.id === item.id.toString());
      })
      .filter(Boolean) as FormField[];
    onChange({ ...form, fields: reorderedFields });
  };

  const handleAddField = () => {
    onChange({ ...form, fields: [...form.fields, defaultField()] });
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "24px",
      }}
    >
      <FormHeader
        title={form.title}
        description={form.description}
        uuid={uuid}
        editable={true}
        onTitleChange={(title) => handleMetaChange("title", title)}
        onDescriptionChange={(description) =>
          handleMetaChange("description", description)
        }
        showViewModeToggle={true}
        viewMode={viewMode}
        setViewMode={setViewMode}
        additionalOptions={additionalOptions}
        showCopyLink={scope !== DocumentScope.PRIVATE}
      />
      {/* Fields List */}
      <Box>
        {form.fields.length === 0 && (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No fields added yet.
          </Typography>
        )}

        {form.fields.length > 0 && (
          <SingleSortableList
            items={form.fields.map((field) => ({
              id: field.id,
              content: (
                <FieldCard
                  key={field.id}
                  field={field}
                  onChange={(newField) => handleFieldChange(field.id, newField)}
                  onRemove={() => handleRemoveField(field.id)}
                />
              ),
            }))}
            onReorder={handleDragReorder}
            handle={true}
          />
        )}
      </Box>

      {/* Add Field Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<Plus size={18} />}
          onClick={handleAddField}
        >
          New question
        </Button>

        <Box
          sx={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button
            disabled={isSubmitting}
            onClick={() => {
              onSubmit();
              pushSnack("Form saved successfully!");
            }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default FormCreator;
